import { notifications }                   from '../ui/Notifications';
import {
  ISubscriptionBatchedRecord,
}                                          from '../DataTypes';

export type BatchedCallbackSubscriptionCallbackFn<T> = (sbr: ISubscriptionBatchedRecord<T>[]) => void;

export abstract class EntityBatchedCallbackSubscriptionModel<T> {
  protected _subscription: any;
  protected _updating: boolean;
  protected _returnSchema: string;
  protected _filter?: Record<string, unknown>;
  protected subscriptionCallback?: BatchedCallbackSubscriptionCallbackFn<T>;

  constructor(subscriptionCallback: BatchedCallbackSubscriptionCallbackFn<T>, options?: {returnSchema?: string, filter?: Record<string, unknown>}) {
    this._subscription        = undefined;
    this._updating            = false;
    this.subscriptionCallback = subscriptionCallback;
    this._returnSchema        = options?.returnSchema || `id`;
    this._filter              = options?.filter;
  }

  set returnSchema(value: string) {
    this._returnSchema = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  get returnSchema(): string {
    return this._returnSchema;
  }

  set filter(value: Record<string, unknown> | undefined) {
    this._filter = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  get filter(): Record<string, unknown> | undefined {
    return this._filter;
  }

  protected abstract subscribe(returnSchema: string, filter: Record<string, unknown>, callback: any): any

  reset() {
    this.stopUpdate();
  }

  sort(entityBatchedRecords: ISubscriptionBatchedRecord<T>[]) {
    const compareFn = (r1: ISubscriptionBatchedRecord<T>, r2: ISubscriptionBatchedRecord<T>) => {
      return parseInt(r1.timestamp) - parseInt(r2.timestamp);
    };

    entityBatchedRecords.sort(compareFn);
  }

  protected getSubscriptionCallback() {
    return async (entityBatchedRecords: ISubscriptionBatchedRecord<T>[]) => {
      if (!entityBatchedRecords?.length) return;
      this.sort(entityBatchedRecords);
      this.subscriptionCallback?.(entityBatchedRecords);
    };
  }

  setSubscriptionCallback(value: BatchedCallbackSubscriptionCallbackFn<T>) {
    this.subscriptionCallback = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  async startUpdate(): Promise<boolean> {
    if (this._updating || this._subscription) return false;
    if (!this.filter) {
      notifications.warning('cannot subscribe without a filter!');
      return false;
    }
    this._updating     = true;
    const callback     = this.getSubscriptionCallback();
    this._subscription = this.subscribe(this.returnSchema, this.filter, callback);
    this._updating     = false;
    return true;
  }

  stopUpdate() {
    if (!this._subscription) {
      return false;
    }
    this._subscription?.unsubscribe();
    this._subscription = undefined;
    return true;
  }

  async refreshUpdate(): Promise<boolean> {
    this.stopUpdate();
    return await this.startUpdate();
  }
}

export default abstract class EntityBatchedCallbackSubscriptionService<T, TBatchedCallbackSubscriptionModel extends EntityBatchedCallbackSubscriptionModel<T>> {
  subscriptionModels: EntityBatchedCallbackSubscriptionModel<T>[];

  constructor() {
    this.subscriptionModels = [];
  }

  createSubscriptionModel(subscriptionCallback?: BatchedCallbackSubscriptionCallbackFn<T>, options?: {returnSchema?: string, filter?: any }): TBatchedCallbackSubscriptionModel {
    const newSubscriptionModel = this._createSubscriptionModel(subscriptionCallback, options);

    this.subscriptionModels.push(newSubscriptionModel);
    return newSubscriptionModel;
  }

  protected abstract _createSubscriptionModel(subscriptionCallback?: BatchedCallbackSubscriptionCallbackFn<T>, options?: {returnSchema?: string, filter?: Record<string, unknown>}): TBatchedCallbackSubscriptionModel

  stopUpdates() {
    this.subscriptionModels.forEach(m => {
      m.stopUpdate();
    });
  }
}
